import type { AdminOverviewResponse } from '@web/shared';

/** Overview-count keys that represent "work waiting in this queue". */
export type PendingKey =
  'openReports' | 'openAppeals' | 'pendingSpots' | 'pendingCircles' | 'pendingPro';

/**
 * The admin navigation model. Grouped for the `lg+` sidebar; flattened for the
 * mobile pill nav. Adding a surface = add a route in `app/router.tsx`, a panel in
 * `features/admin/panels/`, and an entry here.
 */
export interface AdminNavItem {
  to: string;
  label: string;
  /** Match exactly (for the index route) instead of by prefix. */
  exact?: boolean;
  /** When set, the sidebar shows a count from `GET /api/admin/overview` so a
   * moderator can see pending work without opening each screen. */
  pendingKey?: PendingKey;
}

/** Pending count for a nav item, or 0. */
export function pendingCount(
  item: AdminNavItem,
  overview: AdminOverviewResponse | undefined,
): number {
  if (!item.pendingKey || !overview) return 0;
  return overview[item.pendingKey] ?? 0;
}

export interface AdminNavGroup {
  title: string;
  items: AdminNavItem[];
}

export const ADMIN_NAV: AdminNavGroup[] = [
  {
    title: 'Queues',
    items: [
      { to: '/admin/reports', label: 'Reports', pendingKey: 'openReports' },
      { to: '/admin/appeals', label: 'Appeals', pendingKey: 'openAppeals' },
    ],
  },
  {
    title: 'People',
    items: [
      { to: '/admin/users', label: 'Users' },
      { to: '/admin/moderators', label: 'Moderators' },
    ],
  },
  {
    title: 'Places',
    items: [
      { to: '/admin/spots', label: 'Spots', pendingKey: 'pendingSpots' },
      { to: '/admin/rooms', label: 'Circles', pendingKey: 'pendingCircles' },
    ],
  },
  {
    title: 'Access',
    items: [{ to: '/admin/pro', label: 'Pro', pendingKey: 'pendingPro' }],
  },
  {
    title: 'System',
    items: [
      { to: '/admin', label: 'Overview', exact: true },
      { to: '/admin/media', label: 'Media' },
      { to: '/admin/metrics', label: 'Metrics' },
      { to: '/admin/broadcast', label: 'Broadcast' },
      { to: '/admin/config', label: 'Config' },
      { to: '/admin/audit', label: 'Audit' },
    ],
  },
];

export const ADMIN_NAV_FLAT: AdminNavItem[] = ADMIN_NAV.flatMap((g) => g.items);

/** Is `pathname` on `item`? */
export function isActive(item: AdminNavItem, pathname: string): boolean {
  return item.exact ? pathname === item.to : pathname.startsWith(item.to);
}
